import { calculateDirection, geoDistance, reverseDirection } from "~/helpers/geoDistance";
import Radio from "~/types/radio";
import { PAL, PalConflict, PalProximityRegion, RadioPalConfict } from "~/types/PAL";
import { ThirdPartyCBRS } from "~/types/ThirdParty";
import { getFrequencyFromEarfcn } from "~/views/radios/radioHelper";

const MAX_DISTANCE_INDOOR = 100; // meters
const MAX_DISTANCE_OUTDOOR = 1500; // meters
export const MAX_DISTANCE_NEARBY = 5000; // meters

/**
 * Determines if a radio is in conflict with any CBRS units.
 * A conflict occurs if:
 *   1. The radio and CBRS are within range of each other,
 *   2. The radio's direction covers the CBRS,
 *   3. The CBRS's direction (reverse) covers the radio,
 *   4. There is frequency overlap.
 *
 * @param radio - The radio to check for conflicts.
 * @param cbrs - Array of CBRS units to check against.
 * @returns Array of conflict objects (one per conflicting CBRS unit).
 */
export function isRadioInConflict(radio: Radio, cbrs: ThirdPartyCBRS[]) {
  // Get radio location, defaulting to 0 if missing
  const radioLocation = {
    lat: radio.location?.latitude ?? 0,
    lng: radio.location?.longitude ?? 0,
  };

  // Determine radio type and range
  const isIndoor = radio.model === "Indoor" || radio.sasConfig?.cbsdCategory === "A";
  const radioRadius = isIndoor ? MAX_DISTANCE_INDOOR : MAX_DISTANCE_OUTDOOR;

  // Get all center frequencies for the radio (one per cell)
  const radioCenterFrequencies =
    radio.cellParameters?.map((cell, i) => {
      const runtimeCell = radio.runtimeInfo?.cellParameters?.[i];
      const earfcn = runtimeCell?.earfcn ?? cell.earfcn?.[0];
      return getFrequencyFromEarfcn(earfcn, cell.band!);
    }).filter(Boolean) ?? [];

  // Build radio sector ranges (one per cell). Depends only on the radio, so it is built once here
  // rather than rebuilt for every CBRS unit examined below (MCS-4316).
  const radioRanges =
    radio.cellParameters?.map((cell) => {
      const azimuth = cell.azimuth ?? 0;
      const beamwidth = cell.beamwidth ?? 0;
      return {
        low: azimuth - beamwidth / 2,
        high: azimuth + beamwidth / 2,
      };
    }) ?? [];

  // Check each CBRS unit for conflict
  const conflicts = cbrs
    .map((cbrsUnit) => {
      // Get CBRS location, defaulting to 0 if missing
      const cbrsLocation = {
        lat: cbrsUnit.location?.latitude ?? 0,
        lng: cbrsUnit.location?.longitude ?? 0,
      };

      // Calculate distance between radio and CBRS
      const distance = geoDistance(
        radioLocation.lat,
        radioLocation.lng,
        cbrsLocation.lat,
        cbrsLocation.lng
      );

      // Determine CBRS range based on type
      const cbrsRadius =
        cbrsUnit.cbsdType === "Cat B" ? MAX_DISTANCE_OUTDOOR : MAX_DISTANCE_INDOOR;

      // 1. Check if within combined range
      if (distance > radioRadius + cbrsRadius) return undefined;

      // 2. Check if radio's direction covers the CBRS
      const radioDirection = calculateDirection(
        radioLocation.lat,
        radioLocation.lng,
        cbrsLocation.lat,
        cbrsLocation.lng
      );

      // Is the CBRS within any radio sector?
      const isRadioInRange = radioRanges.some((range) =>
        range.low <= radioDirection.bearing && range.high >= radioDirection.bearing);
      if (!isRadioInRange) return undefined;

      // 3. Check if CBRS's direction (reverse) covers the radio
      const cbrsAzimuth = cbrsUnit.installParams?.azimuth ?? 0;
      const cbrsBeamwidth = cbrsUnit.installParams?.beamwidth ?? 0;
      const cbrsRanges = {
        low: cbrsAzimuth - cbrsBeamwidth / 2,
        high: cbrsAzimuth + cbrsBeamwidth / 2,
      };
      const reverse = reverseDirection(radioDirection.bearing);
      const isCbrsInRange = cbrsRanges.low <= reverse.bearing && cbrsRanges.high >= reverse.bearing;
      if (!isCbrsInRange) return undefined;

      // 4. Check for frequency overlap
      const frequenciesInConflict =
        cbrsUnit.grants?.reduce((acc, grant) => {
          const { low, high } = grant.frequencyRange;
          if (
            radioCenterFrequencies.some(
              (freq) => freq >= low && freq <= high
            )
          ) {
            acc.push({ low, high });
          }
          return acc;
        }, [] as { low: number; high: number }[]);

      if (!frequenciesInConflict.length) return undefined;

      // All checks passed: return conflict object
      return {
        isInConflict: true,
        cbrsUnit,
        distance,
        frequencies: frequenciesInConflict,
        radioDirection,
        radioRadius,
        cbrsRadius,
      };
    })
    .filter((conflict) => conflict !== undefined); // Remove undefined (non-conflicts)

  return conflicts;
}

/**
 * Checks if a point is inside a ring (a closed polygon path).
 * Uses the ray-casting algorithm for point-in-polygon.
 *
 * @param point - The geographic coordinate of the point to check.
 * @param ring - The polygon ring as array of [lng, lat] pairs.
 * @returns True if point is inside the ring, false otherwise.
 */
function isPointInRing(point: { lat: number; lng: number }, ring: number[][]): boolean {
  let inside = false;
  const x = point.lng;
  const y = point.lat;

  // Iterate through each edge of the ring
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    // Extract current and previous vertices
    const xi = ring[i][0];
    const yi = ring[i][1];
    const xj = ring[j][0];
    const yj = ring[j][1];
    // Check if the horizontal ray from the point crosses this edge
    const intersects = ((yi > y) !== (yj > y))
      && (x < ((xj - xi) * (y - yi)) / ((yj - yi) || Number.EPSILON) + xi);
    // If intersection found, toggle the inside status
    if (intersects) {
      inside = !inside;
    }
  }

  return inside;
}

/**
 * Checks if a point is inside a GeoJSON Polygon or MultiPolygon feature.
 * Handles both simple polygons and multipolygons.
 *
 * @param point - The point to check.
 * @param feature - The GeoJSON Polygon or MultiPolygon geometry object.
 * @returns True if the point is inside the feature geometry, false otherwise.
 */
function isPointInsidePolygonFeature(point: { lat: number; lng: number }, feature: PAL["countyPolygon"]): boolean {
  const { geometry } = feature;

  // If the geometry is a Polygon, check its rings
  if (geometry.type === "Polygon") {
    // Some rings might be holes; the logic here follows GeoJSON semantics
    return geometry.coordinates.some((ring) => isPointInRing(point, ring));
  }

  // If geometry is MultiPolygon, check every ring in every polygon
  return geometry.coordinates.some((polygon) =>
    polygon.some((ring) => isPointInRing(point, ring))
  );
}

/**
 * Client-side fallback PAL conflict detection. Emits the same `PalConflict`
 * shape as the backend (per matching cell) so both sources render identically.
 */
export function isRadioInPalConflict(radio: Radio, palData: PAL[]): PalConflict[] {
  const radioLocation = {
    lat: radio.location?.latitude ?? 0,
    lng: radio.location?.longitude ?? 0,
  };

  const cellFrequencies = radio.cellParameters?.map((cell, cellIndex) => {
    const runtimeCell = radio.runtimeInfo?.cellParameters?.[cellIndex];
    const earfcn = runtimeCell?.earfcn ?? cell.earfcn?.[0];
    return { cellIndex, earfcn: earfcn ?? 0, frequencyMHz: getFrequencyFromEarfcn(earfcn, cell.band!) };
  }).filter((cell) => Boolean(cell.frequencyMHz)) ?? [];

  // `palData` is aggregated per selected site, so the same county can appear
  // multiple times. Track seen county/state pairs to avoid duplicate conflicts.
  const seenCounties = new Set<string>();
  const conflicts: PalConflict[] = [];

  palData.forEach((palRecord) => {
    const countyKey = `${palRecord.state}-${palRecord.county}`;
    if (seenCounties.has(countyKey)) return;
    if (!isPointInsidePolygonFeature(radioLocation, palRecord.countyPolygon)) return;

    cellFrequencies.forEach(({ cellIndex, earfcn, frequencyMHz }) => {
      const palChannels = palRecord.palChannels.filter(
        (channel) => frequencyMHz >= channel.lowFrequency && frequencyMHz <= channel.highFrequency
      );
      if (!palChannels.length) return;

      seenCounties.add(countyKey);
      conflicts.push({
        cellIndex,
        county: palRecord.county,
        state: palRecord.state,
        earfcn,
        frequencyMHz,
        palChannels,
      });
    });
  });

  return conflicts;
}

/**
 * Conflict state for a Highway9 radio. Single source of truth shared by the map
 * pin tooltip (`getHighway9Pin`), the side-panel `MarkerCard`, and the tab-level
 * conflict counter/county highlight. Backend and client PAL conflicts share the
 * same `PalConflict` shape, so `palConflicts` holds whichever source is active.
 */
export interface RadioConflictState {
  cbrsConflicts: ReturnType<typeof isRadioInConflict>;
  palConflicts: PalConflict[];
  proximityRegions: PalProximityRegion[];
  /** True when the pin should display a warning badge. */
  hasWarning: boolean;
}

/**
 * Resolves the backend-computed PAL conflict record for a given radio from the
 * per-radio list returned by the API.
 */
export function resolveRadioPalConflict(
  palConflict: RadioPalConfict[] | undefined,
  radio: Radio,
): RadioPalConfict | undefined {
  if (!palConflict?.length || !radio.id) return undefined;
  return palConflict.find((c) => c.radioId === radio.id);
}

/**
 * Computes the conflict state for a radio. Backend-computed PAL data is
 * authoritative: when it is available the client-side heuristic is never run
 * (a radio with no backend record simply has no PAL conflict). The client-side
 * point-in-polygon heuristic is used only as a fallback when backend PAL data
 * is unavailable.
 *
 * @param hasBackendPalData - Whether backend PAL conflict data is available at
 *   all (for any radio). Defaults to whether this radio has a record.
 */
export function getRadioConflictState(
  radio: Radio,
  cbrsData: ThirdPartyCBRS[] = [],
  palData: PAL[] = [],
  palConflictRecord?: RadioPalConfict,
): RadioConflictState {
  const cbrsConflicts = cbrsData.length > 0 ? isRadioInConflict(radio, cbrsData) : [];

  const palConflicts = palConflictRecord?.palConflicts ? palConflictRecord.palConflicts :
    palData.length > 0
      ? isRadioInPalConflict(radio, palData)
      : [];

  const proximityRegions = palConflictRecord?.palProximityRegions ? palConflictRecord.palProximityRegions : [];

  return {
    cbrsConflicts,
    palConflicts,
    proximityRegions,
    hasWarning: cbrsConflicts.length > 0 || palConflicts.length > 0,
  };
}

/** Conflict state used when a radio has no precomputed entry. */
export const EMPTY_RADIO_CONFLICT_STATE: RadioConflictState = {
  cbrsConflicts: [],
  palConflicts: [],
  proximityRegions: [],
  hasWarning: false,
};

export interface FilteredPlanningCBRS {
  /** CBRS units that pass the active source/zone filters (normal + defunct). */
  all: ThirdPartyCBRS[];
  /** Active non-defunct CBRS units. */
  normal: ThirdPartyCBRS[];
  /** Active defunct CBRS units (any grant with negative power). */
  defunct: ThirdPartyCBRS[];
}

/**
 * Filters CBRS units by the active source toggles and zone types. Shared by the
 * map tab (for counts and the visible CBRS set) and the conflict selector so
 * both apply identical filtering.
 */
export function filterPlanningCBRS(
  cbrs: ThirdPartyCBRS[] | undefined,
  source: string[],
  zoneType: string[],
): FilteredPlanningCBRS {
  const normal: ThirdPartyCBRS[] = [];
  const defunct: ThirdPartyCBRS[] = [];
  const all: ThirdPartyCBRS[] = [];

  cbrs?.forEach((cbrsItem) => {
    const type = cbrsItem.cbsdType === "Cat B" ? "outdoor" : "indoor";
    if (!zoneType.includes(type)) return;
    // Check if any grant has negative power (defunct)
    const isDefunct = cbrsItem.grants?.some((grant) => (grant.power ?? 0) < 0);

    if (isDefunct) {
      if (source.includes("cbrsDefunct")) {
        defunct.push(cbrsItem);
        all.push(cbrsItem);
      }
    } else {
      if (source.includes("cbrs")) {
        normal.push(cbrsItem);
        all.push(cbrsItem);
      }
    }
  });

  return { all, normal, defunct };
}
